import { SelectionMode } from "@xyflow/react";
import type { CanvasCursorTool } from "./canvas-controls.types";

// ---------------------------------------------------------------------------
// Canvas controls configuration
//
// All behavioural constants for mouse, touchpad, keyboard, and grid controls
// live here. Handler logic stays in CanvasPage.tsx; only change *what*
// triggers something here.
// ---------------------------------------------------------------------------

export const CANVAS_PAN = {
  /**
   * Mouse buttons that trigger panning when dragged.
   * 1 = middle mouse button, 2 = right mouse button.
   */
  mouseButtons: [1, 2] as number[],
  /** Two-finger scroll (touchpad) and scroll wheel pan the viewport. */
  onScroll: true,
} as const;

export const CANVAS_ZOOM = {
  /** Scroll wheel alone does NOT zoom (it pans instead via CANVAS_PAN.onScroll). */
  onScroll: false,
  /** Pinch gesture on touchpad zooms. */
  onPinch: true,
  /** Double-click does NOT zoom. */
  onDoubleClick: false,
  minZoom: 0.1,
  maxZoom: 2,
} as const;

export const CANVAS_SELECTION = {
  mode: SelectionMode.Partial,
  /** Modifier keys that toggle individual node selection (Cmd, Ctrl, or Shift). */
  multiSelectionKeys: ["Meta", "Control", "Shift"],
} as const;

/** React Flow interaction flags per top-right cursor tool (Arrow vs Pointer). */
export interface CanvasCursorInteraction {
  panOnDragMouseButtons: number[];
  selectionOnDrag: boolean;
  elementsSelectable: boolean;
  nodesDraggable: boolean;
  nodesConnectable: boolean;
  /** Pane click may select sections / change selection (Arrow only). */
  paneSelectsOnClick: boolean;
}

export function getCanvasCursorInteraction(
  tool: CanvasCursorTool,
): CanvasCursorInteraction {
  if (tool === "hand") {
    return {
      /** Left, middle, and right drag pan — Figma Pointer / Hand tool. */
      panOnDragMouseButtons: [0, 1, 2],
      selectionOnDrag: false,
      elementsSelectable: false,
      nodesDraggable: false,
      nodesConnectable: false,
      paneSelectsOnClick: false,
    };
  }

  return {
    panOnDragMouseButtons: [...CANVAS_PAN.mouseButtons],
    selectionOnDrag: true,
    elementsSelectable: true,
    nodesDraggable: true,
    nodesConnectable: true,
    paneSelectsOnClick: true,
  };
}

/**
 * THE canvas lattice — the one grid number in the system. Positions,
 * dimensions, gaps, and endpoints all snap to multiples of it; the
 * rendered grid draws at it (every visible line is a real snap target).
 * Ruling: docs/features/2026-08-10-canvas-objects-redesign/
 * canvas-lattice-ruling.md (backend repo).
 */
export const LATTICE = 16;

export const CANVAS_SNAP = {
  enabled: true,
  grid: [LATTICE, LATTICE] as [number, number],
} as const;

/** Dot grid rendering — matches dashboard-comp / showcase `canvas.css`. */
export const CANVAS_GRID = {
  /** Dot diameter at zoom 1 (1.2px radius). */
  dotSize: 2.4,
  /** Glow-layer dot diameter at zoom 1 (1.4px radius). */
  dotGlowSize: 2.8,
} as const;

/** Spacing between nodes (vertical and horizontal). Matches grid for consistent layout. */
export const CANVAS_SPACING = {
  nodeGap: LATTICE,
} as const;

/**
 * Snap a value to the nearest multiple of `step`.
 * Shared across all canvas placement utilities so there is a single source
 * of truth for the grid-snap math.
 */
export const snapToGrid = (val: number, step: number): number =>
  Math.round(val / step) * step;

export const CANVAS_KEYS = {
  quickChat: "c",
  voiceInput: "v",
  addTextLabel: "t",
  addNote: "n",
  addDoc: "d",
  addLink: "l",
  uploadFile: "u",
  addWidgets: "w",
  addShapes: "p",
  addSection: "s",
  cancel: "Escape",
  delete: ["Delete", "Backspace"] as string[],
  stopVoiceRecording: [" ", "Enter"] as string[],
  undo: "z",
  redo: "z",
  zoomIn: "=",
  zoomOut: "-",
  zoomToFit: "1",
  toggleExplorer: "e",
} as const;
